import { useCallback } from 'react'

import { useRouter } from 'next/router'

import {
  describeSaleGiftBridge,
  isInAppWebView,
  logBridgeHandlerError,
  logBridgePurchaseRequest,
  logBridgePurchaseResult,
  logBridgePurchaseWaiting,
  logNativeInit,
} from '~/libs/sentryGift'
import { useApplication } from '~/provider/ApplicationProvider'
import { useSession } from '~/provider/SessionProvider'

type AppHandlerType =
  | 'inAppReady'
  | 'delegateOpenUrl'
  | 'delegateSignIn'
  | 'delegateReloadUnreadBadgeList'
  | 'delegateSaleGiftsForChatSpace'
  | 'delegateSaleGiftsForTweet'
  | 'delegateSaleGiftsForReply'

const callAppHandler = (handler: AppHandlerType, ...args: any[]) => {
  let sale: ReturnType<typeof describeSaleGiftBridge>
  try {
    sale = describeSaleGiftBridge(handler, args)
    // Web では flutter bridge が来ないので、待っていること自体は記録しない。
    if (
      sale &&
      isInAppWebView() &&
      typeof window.flutter_inappwebview?.callHandler !== 'function'
    ) {
      logBridgePurchaseWaiting(sale)
    }
  } catch (error) {
    console.error(error)
  }

  const i = setInterval(() => {
    if (typeof window?.flutter_inappwebview?.callHandler === 'function') {
      clearInterval(i)
      let result: unknown
      try {
        result = window.flutter_inappwebview.callHandler(handler, ...args)
      } catch (error) {
        try {
          if (sale) logBridgeHandlerError(sale, error)
        } catch (loggingError) {
          console.error(loggingError)
        }
        throw error
      }
      try {
        if (sale == null) return
        const request = sale
        logBridgePurchaseRequest(request)
        // 元の rejection は握りつぶさず、ログのあとで再び投げる。
        Promise.resolve(result).then(undefined, (error) => {
          try {
            logBridgeHandlerError(request, error)
          } catch (loggingError) {
            console.error(loggingError)
          }
          throw error
        })
      } catch (loggingError) {
        console.error(loggingError)
      }
    }
  }, 200)
}

export const flutterInAppReady = () => {
  callAppHandler('inAppReady')
}

export const useFlutterCallbacks = () => {
  const app = useApplication()
  const session = useSession()
  const router = useRouter()

  const registerFlutterCallbacks = useCallback(() => {
    window.inapp = {
      init: (
        accessToken: string,
        userId: string,
        redirectPath: string,
        options?: { iapGiftEnabled?: boolean; faqEnabled?: boolean }
      ) => {
        app.setIapGiftEnabled(options?.iapGiftEnabled ?? false)
        app.setFaqEnabled(options?.faqEnabled ?? false)
        session
          .setNewSession(userId, accessToken)
          .then(() => {
            router.replace(redirectPath)
          })
          .catch((e) => {
            console.error(e)
            window?.inapp?.delegateSignIn?.()
          })
        try {
          logNativeInit({
            iapGiftEnabled: options?.iapGiftEnabled ?? false,
            faqEnabled: options?.faqEnabled ?? false,
            redirectPath,
            userId,
          })
        } catch (error) {
          console.error(error)
        }
      },
      setLocale: (locale: string) => {
        app.setLocale(locale)
      },
      saleGiftsCallback: (
        result: 'purchased' | 'error' | 'canceled',
        ...extra: unknown[]
      ) => {
        app.setSaleGiftsResult(result)
        try {
          logBridgePurchaseResult(result, extra)
        } catch (error) {
          console.error(error)
        }
      },
      setInstalledApps: (apps: { outlook?: boolean }) => {
        app.setInstalledApps(apps)
      },
      delegateOpenUrl: (url: string) => callAppHandler('delegateOpenUrl', url),
      delegateSignIn: () => callAppHandler('delegateSignIn'),
      delegateReloadUnreadBadgeList: () =>
        callAppHandler('delegateReloadUnreadBadgeList'),
      delegateSaleGiftsForChatSpace: (chatSpaceId: string, toUserId: string) =>
        callAppHandler('delegateSaleGiftsForChatSpace', chatSpaceId, toUserId),
      delegateSaleGiftsForTweet: (tweetId: string) =>
        callAppHandler('delegateSaleGiftsForTweet', tweetId),
      delegateSaleGiftsForReply: (tweetReplyId: string) =>
        callAppHandler('delegateSaleGiftsForReply', tweetReplyId),
    }
  }, [app, router, session])

  return registerFlutterCallbacks
}
